import * as React from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  Typography,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import Menu from "../../../../models/Menu";
import GridViewIcon from "@mui/icons-material/GridView";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

interface AppSideDrawerProps {
  open: boolean;
  handleDrawerClose: () => void;
  menus: Menu[];
}

const drawerWidth = 240;

// Sidebar design tokens
const SB = {
  bg: "#1C1B3B",
  hoverBg: "rgba(255,255,255,0.06)",
  activeBg: "rgba(99,91,237,0.22)",
  activeBorder: "#6355E7",
  text: "#BDB9D7",
  activeText: "#FFFFFF",
  icon: "#7B77A8",
  activeIcon: "#A89CF7",
  subText: "#9592B8",
  activeSubText: "#E0DEFC",
  divider: "rgba(255,255,255,0.08)",
  sectionLabel: "#6B678F",
};

const AppSideDrawer: React.FC<AppSideDrawerProps> = ({
  open,
  handleDrawerClose,
  menus,
}) => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [openMenu, setOpenMenu] = React.useState<Record<number, boolean>>({});

  const handleMenuClick = (index: number) => {
    setOpenMenu((prev) => ({
      [index]: !prev[index],
    }));
  };

  const isMenuActive = (menuPath: string) => {
    if (menuPath === "") return location.pathname === "/";
    if (!location.pathname.startsWith(menuPath)) return false;

    const moreSpecificMenuMatches = menus.some((other) => {
      if (!other?.path || other.path === menuPath) return false;
      if (
        !other.path.startsWith(menuPath) ||
        other.path.length <= menuPath.length
      ) {
        return false;
      }
      return location.pathname.startsWith(other.path);
    });

    return !moreSpecificMenuMatches;
  };

  const isSubMenuActive = (menuPath: string, subPath: string) => {
    const full = (menuPath + subPath).split("?")[0];
    return (
      location.pathname === full || location.pathname.startsWith(full + "/")
    );
  };

  // Section labels to insert before certain menu titles
  const getSectionLabel = (title: string): string | null => {
    const labels: Record<string, string> = {
      Dashboard: "Dashboards",
      "Cust. Exp Dashboard": "Dashboards",
      "Finance Dashboard": "Dashboards",
      "Logistics Dashboard": "Dashboards",
      "NPS Dashboard": "Dashboards",
      "Redemption Dashboard": "Dashboards",
      "Alerts Dashboard": "Dashboards",
      "Ops Dashboard": "Dashboards",
      "Partner Dashboard": "Dashboards",
      "Sales Dashboard": "Dashboards",
      Address: "Sales",
      Customers: "Sales",
      Leads: "Sales",
      Orders: "Sales",
      Property: "Sales",
      "Urgent Sourcing": "Sales",
      Users: "Sales",
      Emailers: "Projects & Operations",
      Inventory: "Projects & Operations",
      Projects: "Projects & Operations",
      Vouchers: "Projects & Operations",
      Products: "Catalog",
      Categories: "Catalog",
      "Swageazy Inventory": "Catalog",
      Invoices: "Finance",
      "Pr & Po": "Finance",
      Vendors: "Finance",
      Employees: "Workspace",
      "Task Tracker": "Workspace",
      Campaigns: "Admin",
      HRMS: "Admin",
      Swagstore: "Admin",
      Tools: "Admin",
    };
    return labels[title] || null;
  };

  const renderedLabels = new Set<string>();

  const drawerContent = (
    <>
      <Box sx={{ pt: 1.5 }} />
      <List disablePadding sx={{ px: 1 }}>
        {menus.map((menu, index) => {
          const active = isMenuActive(menu.path);
          const hasSub = menu.subTitle && menu.subTitle.length > 0;
          const isExpanded = openMenu[index] === true;

          const sectionLabel = getSectionLabel(menu.title);
          const showLabel = sectionLabel && !renderedLabels.has(sectionLabel);
          if (showLabel && sectionLabel) renderedLabels.add(sectionLabel);

          return (
            <React.Fragment key={menu.title}>
              {/* Section divider + label */}
              {showLabel && (
                <Box sx={{ pt: index === 0 ? 0 : 2, pb: 0.5, px: 1 }}>
                  {index !== 0 && (
                    <Box sx={{ height: "1px", bgcolor: SB.divider, mb: 1.5 }} />
                  )}
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: SB.sectionLabel,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      fontFamily: "Inter, sans-serif",
                      px: 0.5,
                    }}
                  >
                    {sectionLabel}
                  </Typography>
                </Box>
              )}

              {/* Menu item without submenu */}
              {!hasSub && (
                <Tooltip
                  title={menu.title}
                  placement="right"
                  disableHoverListener
                >
                  <Link
                    to={menu.path}
                    style={{ textDecoration: "none", color: "inherit" }}
                    onClick={isMobile ? handleDrawerClose : undefined}
                  >
                    <ListItem disablePadding sx={{ mb: 0.25 }}>
                      <ListItemButton
                        onClick={() => handleMenuClick(index)}
                        sx={{
                          borderRadius: "8px",
                          px: 1.25,
                          py: 0.75,
                          minHeight: 40,
                          position: "relative",
                          bgcolor: active ? SB.activeBg : "transparent",
                          borderLeft: active
                            ? `3px solid ${SB.activeBorder}`
                            : "3px solid transparent",
                          "&:hover": {
                            bgcolor: active ? SB.activeBg : SB.hoverBg,
                          },
                          transition: "all 0.15s ease",
                        }}
                      >
                        <Box
                          sx={{
                            mr: 1.25,
                            fontSize: 14,
                            color: active ? SB.activeIcon : SB.icon,
                            display: "flex",
                            alignItems: "center",
                            minWidth: 18,
                            justifyContent: "center",
                            transition: "color 0.15s",
                          }}
                        >
                          {menu.icon ? (
                            menu.icon
                          ) : (
                            <GridViewIcon sx={{ fontSize: 16 }} />
                          )}
                        </Box>
                        <ListItemText
                          primary={menu.title}
                          primaryTypographyProps={{
                            fontSize: 13,
                            fontWeight: active ? 600 : 500,
                            color: active ? SB.activeText : SB.text,
                            fontFamily: "Inter, sans-serif",
                            letterSpacing: "-0.005em",
                            noWrap: true,
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  </Link>
                </Tooltip>
              )}

              {/* Menu item with submenu */}
              {hasSub && (
                <>
                  <ListItem disablePadding sx={{ mb: 0.25 }}>
                    <ListItemButton
                      onClick={() => handleMenuClick(index)}
                      sx={{
                        borderRadius: "8px",
                        px: 1.25,
                        py: 0.75,
                        minHeight: 40,
                        bgcolor:
                          active && !isExpanded ? SB.activeBg : "transparent",
                        borderLeft:
                          active && !isExpanded
                            ? `3px solid ${SB.activeBorder}`
                            : "3px solid transparent",
                        "&:hover": {
                          bgcolor:
                            active && !isExpanded ? SB.activeBg : SB.hoverBg,
                        },
                        transition: "all 0.15s ease",
                      }}
                    >
                      <Box
                        sx={{
                          mr: 1.25,
                          fontSize: 14,
                          color: active ? SB.activeIcon : SB.icon,
                          display: "flex",
                          alignItems: "center",
                          minWidth: 18,
                          justifyContent: "center",
                          transition: "color 0.15s",
                        }}
                      >
                        {menu.icon ? (
                          menu.icon
                        ) : (
                          <GridViewIcon sx={{ fontSize: 16 }} />
                        )}
                      </Box>
                      <ListItemText
                        primary={menu.title}
                        primaryTypographyProps={{
                          fontSize: 13,
                          fontWeight: active ? 600 : 500,
                          color: active ? SB.activeText : SB.text,
                          fontFamily: "Inter, sans-serif",
                          letterSpacing: "-0.005em",
                          noWrap: true,
                        }}
                      />
                      <Box
                        sx={{
                          color: SB.icon,
                          display: "flex",
                          alignItems: "center",
                          fontSize: 16,
                        }}
                      >
                        {isExpanded ? (
                          <ExpandLessIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <ExpandMoreIcon sx={{ fontSize: 16 }} />
                        )}
                      </Box>
                    </ListItemButton>
                  </ListItem>

                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List
                      component="div"
                      disablePadding
                      sx={{ pl: 1, mb: 0.5 }}
                    >
                      {menu.subTitle?.map((subMenu) => {
                        const subActive = isSubMenuActive(
                          menu.path,
                          subMenu.path,
                        );
                        return (
                          <Link
                            key={subMenu.title}
                            to={menu.path + subMenu.path}
                            style={{ textDecoration: "none", color: "inherit" }}
                            onClick={isMobile ? handleDrawerClose : undefined}
                          >
                            <ListItemButton
                              sx={{
                                borderRadius: "7px",
                                pl: 1.5,
                                pr: 1,
                                py: 0.6,
                                minHeight: 36,
                                mb: 0.15,
                                bgcolor: subActive
                                  ? "rgba(99,91,237,0.18)"
                                  : "transparent",
                                borderLeft: subActive
                                  ? `2px solid ${SB.activeBorder}`
                                  : "2px solid transparent",
                                "&:hover": {
                                  bgcolor: subActive
                                    ? "rgba(99,91,237,0.18)"
                                    : "rgba(255,255,255,0.04)",
                                },
                                transition: "all 0.12s ease",
                              }}
                            >
                              <Box
                                sx={{
                                  mr: 1,
                                  fontSize: 12,
                                  color: subActive ? SB.activeIcon : SB.icon,
                                  display: "flex",
                                  alignItems: "center",
                                  minWidth: 16,
                                  justifyContent: "center",
                                  opacity: 0.8,
                                }}
                              >
                                {subMenu.icon ? (
                                  subMenu.icon
                                ) : (
                                  <GridViewIcon sx={{ fontSize: 12 }} />
                                )}
                              </Box>
                              <ListItemText
                                primary={subMenu.title}
                                primaryTypographyProps={{
                                  fontSize: 12,
                                  fontWeight: subActive ? 600 : 400,
                                  color: subActive
                                    ? SB.activeSubText
                                    : SB.subText,
                                  fontFamily: "Inter, sans-serif",
                                  noWrap: true,
                                }}
                              />
                            </ListItemButton>
                          </Link>
                        );
                      })}
                    </List>
                  </Collapse>
                </>
              )}
            </React.Fragment>
          );
        })}
      </List>
      <Box sx={{ pb: 3 }} />
    </>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      {/* Mobile: temporary drawer with backdrop */}
      {isMobile && (
        <Drawer
          variant="temporary"
          anchor="left"
          open={open}
          onClose={handleDrawerClose}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              background: SB.bg,
              border: "none",
              marginTop: "56px",
              height: "calc(100% - 56px)",
              overflowX: "hidden",
              "&::-webkit-scrollbar": { width: "3px" },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(255,255,255,0.12)",
                borderRadius: "4px",
              },
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Desktop: persistent drawer */}
      {!isMobile && (
        <Drawer
          variant="persistent"
          anchor="left"
          open={open}
          sx={{
            display: { xs: "none", md: "block" },
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              maxHeight: `calc(100vh - 56px)`,
              boxSizing: "border-box",
              marginTop: "56px",
              background: SB.bg,
              border: "none",
              overflowX: "hidden",
              "&::-webkit-scrollbar": { width: "3px" },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(255,255,255,0.12)",
                borderRadius: "4px",
              },
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </Box>
  );
};

export default React.memo(AppSideDrawer);
